import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const AuctioneerRequestListing = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    licenseNumber: '',
    state: '',
    city: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    areasOfExpertise: [],
    yearsExperience: '',
    interestedInPremier: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const expertiseAreas = [
    'Real Estate',
    'Farm Equipment',
    'Estate Sales',
    'Business Liquidation',
    'Vehicles',
    'Antiques & Collectibles',
    'Livestock',
    'Construction Equipment',
    'Personal Property',
    'Charity Auctions',
    'Online Auctions',
    'Land & Property',
    'Industrial Equipment',
    'Other'
  ];

  const handleExpertiseToggle = (area) => {
    if (formData.areasOfExpertise.includes(area)) {
      setFormData({
        ...formData,
        areasOfExpertise: formData.areasOfExpertise.filter(a => a !== area)
      });
    } else {
      setFormData({
        ...formData,
        areasOfExpertise: [...formData.areasOfExpertise, area]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auctioneers/request-listing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            full_name: formData.fullName,
            license_number: formData.licenseNumber,
            state: formData.state,
            city: formData.city,
            zip_code: formData.zipCode,
            phone: formData.phone,
            email: formData.email,
            website: formData.website || null,
            areas_of_expertise: formData.areasOfExpertise,
            years_experience: parseInt(formData.yearsExperience),
            interested_in_premier: formData.interestedInPremier
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit request');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                Request Your Listing
              </h1>
              <p className="text-lg text-gray-600">
                Join thousands of licensed auctioneers in our directory and get discovered by clients nationwide!
              </p>
            </div>

            {success ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Submitted!</h2>
                <p className="text-gray-700 mb-6">
                  Thank you for your interest! We'll review your information and contact you within 2-3 business days to complete your listing.
                </p>
                <a
                  href="/find-auctioneer"
                  className="inline-block px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
                >
                  Browse Auctioneers
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}

                {/* Personal Information */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Personal Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="12345"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        License State <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Dallas"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.zipCode}
                        onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="75001"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website <span className="text-gray-500">(Optional)</span>
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Information</h2>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Years of Experience <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="99"
                      value={formData.yearsExperience}
                      onChange={(e) => setFormData({...formData, yearsExperience: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Areas of Expertise <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-3">Select all that apply</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {expertiseAreas.map(area => (
                        <label
                          key={area}
                          className={`flex items-center px-3 py-2 border rounded-lg cursor-pointer transition ${
                            formData.areasOfExpertise.includes(area)
                              ? 'bg-green-50 border-green-500 text-green-900'
                              : 'bg-white border-gray-300 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.areasOfExpertise.includes(area)}
                            onChange={() => handleExpertiseToggle(area)}
                            className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <span className="text-sm">{area}</span>
                        </label>
                      ))}
                    </div>
                    {formData.areasOfExpertise.length === 0 && (
                      <p className="text-sm text-red-500 mt-2">Please select at least one area of expertise</p>
                    )}
                  </div>
                </div>

                {/* Premier Listing Offer */}
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="premier"
                      checked={formData.interestedInPremier}
                      onChange={(e) => setFormData({...formData, interestedInPremier: e.target.checked})}
                      className="mt-1 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="premier" className="ml-3 flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-2">🏆</span>
                        <span className="text-lg font-bold text-gray-900">Yes! I'm interested in Premier Listing</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
                        Get top placement in search results for your city/state for only <strong>$4.99/month</strong> (billed annually at $59.88)
                      </p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>✓ First in search results for your area</li>
                        <li>✓ Featured in regional ads sidebar</li>
                        <li>✓ Custom logo & video showcase</li>
                        <li>✓ Enhanced profile with marketing tagline</li>
                        <li>✓ AI search visibility (ChatGPT & AI assistants)</li>
                      </ul>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading || formData.areasOfExpertise.length === 0}
                    className="w-full px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed text-lg"
                  >
                    {loading ? 'Submitting...' : '🎯 Submit Listing Request'}
                  </button>
                  <p className="text-sm text-gray-600 text-center mt-3">
                    We'll review your information and contact you within 2-3 business days
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
